import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionContext';
import { UserRole, roleColors } from '../../types/permissions';
import { sendToTerminal } from '../../lib/terminalLogger';

interface RoleSelectorProps {
  className?: string;
}

export function RoleSelector({ className = '' }: RoleSelectorProps) {
  const {
    role,
    availableRoles,
    canSwitchRole,
    setActiveRole
  } = usePermissions();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Não mostrar se não pode trocar
  if (!canSwitchRole || availableRoles.length <= 1) {
    return null;
  }

  const handleRoleSelect = (selectedRole: UserRole) => {
    if (selectedRole === role) {
      setIsOpen(false);
      return;
    }

    try {
      setActiveRole(selectedRole);
      setIsOpen(false);

      // Log removido - PermissionContext já loga a mudança de role
      // Reduz verbosidade no terminal mantendo apenas logs essenciais
    } catch (error) {
      sendToTerminal('❌ [ROLE_SELECTOR] Erro ao alterar role', {
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    }
  };

  const roleColor = roleColors[role]

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Badge clicável */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5 px-2.5 py-1.5 rounded-md
          transition-colors duration-200
          ${roleColor.primary} ${roleColor.hover}
          text-white text-xs font-medium
          cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-blue-500/50
        `}
        aria-label={`Role atual: ${role}. Clique para trocar`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="uppercase">{role}</span>
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[160px] bg-navy-800 border border-navy-700 rounded-md shadow-lg overflow-hidden">
          <div className="py-1">
            {availableRoles.map((availableRole) => {
              const isActive = availableRole === role;
              const roleColor = roleColors[availableRole] || "";

              return (
                <button
                  key={availableRole}
                  onClick={() => handleRoleSelect(availableRole)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 text-sm
                    transition-colors duration-150
                    ${isActive
                      ? `${roleColor.primary} text-white`
                      : 'text-gray-300 hover:bg-navy-700'
                    }
                    focus:outline-none focus:bg-navy-700
                  `}
                >
                  <span className="uppercase font-medium">{availableRole}</span>
                  {isActive && (
                    <Check className="w-4 h-4" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

